import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  Link,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { GET_PROCESSORS } from '#/graphql/queries/processors';

export function ProcessorsPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, loading, error, refetch } = useQuery(GET_PROCESSORS);

  const filteredProcessors =
    data?.processors?.processors?.filter((p) =>
      p.id.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  if (loading && !data) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={6}>
          Processors
        </Heading>
        <Text>Loading processors…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={6}>
          Processors
        </Heading>
        <Text color="red.500">Error loading processors: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <VStack gap={4} align="stretch">
        <Heading size="lg" color="fg">
          Processors
        </Heading>
        <Text color="fg.muted" fontSize="sm">
          JavaScript processors stored in the database; each row points at a
          main processor file and optional prepended segments. Manage rows via
          seeding or admin tools outside this app.
        </Text>
        <HStack flexWrap="wrap" gap={2}>
          <Input
            placeholder="Search processors…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="sm"
            maxW="280px"
          />
          <Button onClick={() => refetch()} size="sm">
            Refresh
          </Button>
        </HStack>

        {filteredProcessors.length === 0 ? (
          <Text color="fg.muted">
            {searchTerm
              ? 'No processors match your search.'
              : 'No processors yet.'}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root
              size="sm"
              variant="line"
              css={{
                '& thead tr, & tbody tr': {
                  backgroundColor: 'var(--input-base-background-color)',
                },
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Id</Table.ColumnHeader>
                  <Table.ColumnHeader>Main file</Table.ColumnHeader>
                  <Table.ColumnHeader>Updated</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredProcessors.map((processor) => (
                  <Table.Row key={processor.id}>
                    <Table.Cell fontWeight="medium">{processor.id}</Table.Cell>
                    <Table.Cell
                      fontFamily="mono"
                      fontSize="xs"
                      maxW="280px"
                      wordBreak="break-all"
                    >
                      {processor.fileId}
                    </Table.Cell>
                    <Table.Cell whiteSpace="nowrap" fontSize="sm">
                      {new Date(processor.updatedAt).toLocaleString()}
                    </Table.Cell>
                    <Table.Cell textAlign="end">
                      <Link asChild color="blue.600">
                        <RouterLink to={`/processors/${processor.id}`}>
                          View
                        </RouterLink>
                      </Link>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
